/* eslint-disable */
import Card from 'components/card';
import { useState } from 'react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import useSWR from 'swr';
import { useDebounceValue } from 'usehooks-ts';
import { User } from 'views/types/typeUser';
import Pagination from '../../../../../components/Pagination/Pagination';
import UserHistoryDialog from '../UserHistoryDialog';
import { formatDate } from 'utils/date';
import { usersService } from 'views/services/usersService';
import { formatNumber } from 'utils/formatNumber';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import { EDatePresets } from '../../../../types/date-range-preset.enum';
import {
  PermissionGate,
  ReadPermissionGate,
  UpdatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';

import 'react-datepicker/dist/react-datepicker.css';

enum EUserFilters {
  ALL = 'all',
  WITH_CARD = 'with_cards',
  KYC_VERIFIED = 'kyc_verified',
  KYC_UNVERIFIED = 'kyc_unverified',
  WITH_REFERRALS = 'with_referrals',
}

function UsersTable() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const { canUpdate } = usePermissions();

  const [search, setSearch] = useState('');

  const [page, setPage] = useState(0);

  const [searchDebounce] = useDebounceValue(search, 1000);
  const [stopAutoRefresh, setStopAutRefresh] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [userFilter, setUserFilter] = useState<null | EUserFilters>(null);

  const sortBy = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc ? 'DESC' : 'ASC';

  const filter = userFilter !== EUserFilters.ALL ? userFilter : '';

  const [preset, setPreset] = useState<EDatePresets>(EDatePresets.ALL);
  const [[startDate, endDate], setDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);

  const applyPreset = (p: EDatePresets) => {
    setPreset(p);
    let from: Date | null = null;
    let to: Date | null = null;
    switch (p) {
      case EDatePresets.DAY:
        from = dayjs().startOf('day').toDate();
        to = dayjs().endOf('day').toDate();
        break;
      case EDatePresets.WEEK:
        from = dayjs().startOf('week').toDate();
        to = dayjs().endOf('week').toDate();
        break;
      case EDatePresets.MONTH:
        from = dayjs().startOf('month').toDate();
        to = dayjs().endOf('month').toDate();
        break;
      case EDatePresets.ALL:
      default:
        break;
    }
    setDateRange([from, to]);
    setPage(0);
  };

  const {
    data: tableData,
    isLoading,
    mutate,
  } = useSWR(
    [
      `/users`,
      page,
      searchDebounce,
      itemsPerPage,
      sortBy,
      sortOrder,
      filter,
      preset,
      startDate?.toISOString(),
      endDate?.toISOString(),
    ],
    () => {
      const params: any = {
        page,
        pageSize: itemsPerPage,
        searchValue: searchDebounce,
        sortBy,
        sortOrder,
        filter: {} as Record<string, string>,
      };

      if (sortBy && sortOrder) {
        params.sortBy = `${sortBy}:${sortOrder}`;
      }

      if (filter) {
        params.filter[filter] = 'true';
      }
      if (startDate) {
        params.filter.from = dayjs(startDate).startOf('day').toISOString();
      }
      if (endDate) {
        params.filter.to = dayjs(endDate).endOf('day').toISOString();
      }
      return usersService.getPaginatedUsersList(params);
    },
    {
      refreshInterval: stopAutoRefresh ? null : 5000,
      revalidateOnFocus: !stopAutoRefresh,
    }
  );

  const SortIcon = ({ direction }: { direction?: 'asc' | 'desc' | false }) => {
    const Icon =
      direction === 'asc'
        ? FaSortUp
        : direction === 'desc'
          ? FaSortDown
          : FaSort;

    return (
      <Icon
        className={clsx(
          'text-[10px] transition-colors',
          direction
            ? 'text-blue-500 dark:text-blue-400'
            : 'text-gray-400 dark:text-gray-600'
        )}
      />
    );
  };

  const columns = [
    columnHelper.accessor('coinsFlyId', {
      id: 'coinsFlyId',
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Client id
            <SortIcon direction={sortDirection as 'asc' | 'desc'} />
          </p>
        );
      },
      cell: info => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.cell.getValue()}
        </p>
      ),
      sortUndefined: 'last',
      sortDescFirst: false,
    }),
    columnHelper.accessor('username', {
      id: 'username',
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Telegram Username
            <SortIcon direction={sortDirection} />
          </p>
        );
      },
      cell: info => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.cell.getValue()}
        </p>
      ),
      sortDescFirst: false,
      sortUndefined: 'last',
    }),

    columnHelper.accessor('email', {
      id: 'email',
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Email
            <SortIcon direction={sortDirection as 'asc' | 'desc'} />
          </p>
        );
      },
      cell: info => (
        <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
          {info.cell.getValue() ?? '--'}
        </p>
      ),
      sortDescFirst: false,
      sortUndefined: 'last',
    }),

    columnHelper.accessor('totalInUsdt', {
      id: 'totalBalance',
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Total Balance
            <SortIcon direction={sortDirection} />
          </p>
        );
      },
      cell: info => (
        <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
          {formatNumber(info.cell.getValue())}
        </p>
      ),
      sortDescFirst: false,
      sortUndefined: 'last',
    }),

    columnHelper.accessor('lastActiveAt', {
      id: 'lastActiveAt',
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Last active
            <SortIcon direction={sortDirection as 'asc' | 'desc'} />
          </p>
        );
      },
      cell: info => (
        <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
          {formatDate(info.cell.getValue(), 'DD.MM.YYYY HH:mm')}
        </p>
      ),
    }),

    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Registration Date
            <SortIcon direction={sortDirection as 'asc' | 'desc'} />
          </p>
        );
      },
      cell: info => (
        <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
          {formatDate(info.cell.getValue(), 'DD.MM.YYYY HH:mm')}
        </p>
      ),
    }),
    columnHelper.accessor('userStatus', {
      id: 'userStatus',
      header: ({ column }) => {
        const sortDirection = column.getIsSorted();

        return (
          <p className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-white">
            Status
            <SortIcon direction={sortDirection as 'asc' | 'desc'} />
          </p>
        );
      },
      cell: info => (
        <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
          {info.cell.getValue()?.charAt(0).toUpperCase() +
            info.cell.getValue()?.slice(1)}
        </p>
      ),
      sortingFn: 'alphanumeric',
    }),
  ];

  const [data] = useDebounceValue(tableData?.data ?? [], 100);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
  });

  const totalPages = Math.ceil(
    (tableData?.meta.totalItems ?? 0) / itemsPerPage
  );

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setPage(0);
  };

  return (
    <Card
      extra={
        'relative flex flex-col rounded-[15px] border-[1px] border-gray-200 bg-white bg-clip-border shadow-md shadow-[#F3F3F3] dark:border-[#ffffff33] dark:!bg-navy-800 dark:text-white dark:shadow-none w-full !h-[75vh] sm:overflow-auto mt-9 px-6 pb-6'
      }
    >
      <header className="relative flex flex-col md:flex-row md:items-center gap-4 pt-4">
        <ReadPermissionGate resource={PERMISSION_RESOURCES.FILTER}>
          <div className="flex flex-wrap items-center gap-4 pb-4">
            <select
              value={userFilter ?? EUserFilters.ALL}
              onChange={e => {
                setUserFilter(e.target.value as EUserFilters);
                setPage(0);
              }}
              disabled={
                !canUpdate(PERMISSION_RESOURCES.CLIENTS_LIST) ||
                !canUpdate(PERMISSION_RESOURCES.FILTER)
              }
              className="rounded border px-2 py-1 bg-white dark:bg-navy-900 text-navy-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value={EUserFilters.ALL}>All</option>
              <option value={EUserFilters.WITH_CARD}>Only with cards</option>
              <option value={EUserFilters.KYC_VERIFIED}>
                Only KYC verified
              </option>
              <option value={EUserFilters.KYC_UNVERIFIED}>
                Only KYC unverified
              </option>
              <option value={EUserFilters.WITH_REFERRALS}>
                Only with referrals
              </option>
            </select>

            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={dates => {
                setPreset(EDatePresets.ALL);
                const [rawFrom, rawTo] = dates as [Date, Date];
                const from = rawFrom
                  ? dayjs(rawFrom).startOf('day').toDate()
                  : null;
                const to = rawTo ? dayjs(rawTo).endOf('day').toDate() : null;
                setDateRange([from, to]);
                setPage(0);
              }}
              disabled={
                !canUpdate(PERMISSION_RESOURCES.CLIENTS_LIST) ||
                !canUpdate(PERMISSION_RESOURCES.FILTER)
              }
              isClearable
              placeholderText="Custom range…"
              className="rounded border px-2 py-1 bg-white dark:bg-navy-900 text-navy-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              popperClassName="react-datepicker-popper"
            >
              <div className="flex gap-2 p-2 border-t bg-white">
                <button
                  className="px-2 py-1 bg-gray-100 rounded"
                  onClick={() => applyPreset(EDatePresets.DAY)}
                >
                  Today
                </button>
                <button
                  className="px-2 py-1 bg-gray-100 rounded"
                  onClick={() => applyPreset(EDatePresets.WEEK)}
                >
                  This Week
                </button>
                <button
                  className="px-2 py-1 bg-gray-100 rounded"
                  onClick={() => applyPreset(EDatePresets.MONTH)}
                >
                  This Month
                </button>
              </div>
            </DatePicker>
          </div>
        </ReadPermissionGate>

        <ReadPermissionGate resource={PERMISSION_RESOURCES.SEARCH_BUTTON}>
          <input
            type="text"
            placeholder="Search by Client ID, TG name, Email"
            className="block min-h-7 ml-auto w-full grow rounded-full bg-lightPrimary px-4 text-base font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:h-full sm:w-fit max-w-[25rem] disabled:opacity-50 disabled:cursor-not-allowed"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(0);
            }}
            disabled={
              !canUpdate(PERMISSION_RESOURCES.CLIENTS_LIST) ||
              !canUpdate(PERMISSION_RESOURCES.SEARCH_BUTTON)
            }
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            autoCapitalize="none"
          />
        </ReadPermissionGate>
      </header>
      {!data || (tableData?.data?.length ?? 0) <= 0 ? (
        <tr>
          <td className="absolute left-1/2 -translate-x-1/2" colSpan={7}>
            <p className="py-10 text-center font-bold uppercase">No data</p>
          </td>
        </tr>
      ) : (
        <>
          <div className="mt-8 grow overflow-x-auto custom-scrollbar">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map(header => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className={clsx(
                            'bg-white pb-2 pr-4 pt-4 text-start dark:bg-navy-800',
                            header.column.getCanSort() && 'cursor-pointer'
                          )}
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{ asc: '', desc: '' }[
                              header.column.getIsSorted() as string
                            ] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td
                      className="absolute left-1/2 -translate-x-1/2"
                      colSpan={7}
                    >
                      <p className="flex justify-center py-10">
                        <AiOutlineLoading3Quarters className="h-10 w-10 animate-spin" />
                      </p>
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  !!tableData &&
                  tableData?.data?.length > 0 &&
                  table.getRowModel().rows.map(row => {
                    return (
                      <UserHistoryDialog
                        key={row.id}
                        onOpenChange={val => setStopAutRefresh(val)}
                        user={row.original}
                        refetchUsers={mutate}
                      >
                        <tr className="[&_td]:odd:bg-gray-50 [&_td]:odd:dark:bg-navy-900 hover:cursor-pointer">
                          {row.getVisibleCells().map(cell => (
                            <td
                              key={cell.id}
                              className="min-w-[100px] border-white/0 py-2 pr-4 first:rounded-l-lg last:rounded-r-lg last:pr-0"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      </UserHistoryDialog>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={tableData?.meta.totalItems}
            onPageChange={setPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalName="Total Clients"
          />
        </>
      )}
    </Card>
  );
}

export default UsersTable;
const columnHelper = createColumnHelper<User>();
